import React, {
    useEffect, useState, useContext, useCallback
} from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { Loader, useModal } from '@jutro/components';
import { Wizard } from '@xengage/gw-portals-wizard-react';
import { ViewModelServiceContext, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { messages as platformMessages } from '@xengage/gw-platform-translations';
import { QuoteInfoComponentContext } from 'gw-capability-quoteandbind-common-react';
import { CustomQuoteInfoComponent } from 'ml-capability-quoteandbind-common-react';
import { ReinstatementService } from 'ml-capability-reinstatement';
import { MLModalService } from 'ml-portals-services-react';
import { FormModalPopover } from 'ml-components-platform-react';

import Reinstatement from './model/Reinstatement';
import messages from './ReinstatementWizard.messages';
import wizardConfig from './config/reinstatement-wizard-config.json5';


function ReinstatementWizard(props) {
    const modalApi = useModal();
    const { steps, title } = wizardConfig;
    const [initialSubmission, setInitialSubmission] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const viewModelService = useContext(ViewModelServiceContext);
    const { location, history } = props;
    const { authHeader } = useAuthentication();
    const translator = useTranslator();

    const ModalService = MLModalService();
    useEffect( () => {
        if (!location.state) {
            history.push('/');
            return;
        }
        const {
            state: { reinstatementData }
        } = location;
        if (!reinstatementData) {
            return;
        }
        let submissionVM = viewModelService.create(
            reinstatementData,
            'pc', 
            'metlife.edge.capabilities.reinstatement.dto.ReinstatementChangeDTOML'
        );
        const promise = ReinstatementService.startReinstatement(reinstatementData, authHeader);
        ModalService.showProgressDialog(messages.creatingReinstatement, promise);
        promise.then((result) => {
            submissionVM = viewModelService.create(
                new Reinstatement(result),
                'pc', 
                'metlife.edge.capabilities.reinstatement.dto.ReinstatementChangeDTOML'
            );
            setInitialSubmission(submissionVM);
            setIsLoading(false);
        });
            
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOnCancel = useCallback((wizardProps) => {
        const {currentStepIndex, wizardData, param: nextPath = ''} = wizardProps;
        const currentStep = steps[currentStepIndex].mlStepName;
        if(currentStep === 'reinstatementConfirmation')  return true;
        if(nextPath.indexOf('reinstatementContactUs') > -1)  return true;

        const componentProps = {
            title: translator(messages.cancelPromptTitle),
            actionBtnLabel: platformMessages.yes,
            cancelBtnLabel: platformMessages.cancelModel,
            simpleMessage: translator(messages.cancelPrompt),
            closeSimpleMessage: false
        };

        return modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).then(() => {

            const jobNumber = _.get(wizardData, 'submissionVM.jobNumber.value');
            const model = _.get(wizardData,  'submissionVM.value');
            if(jobNumber){
                const promise = ReinstatementService.withdrawReinstatement(model, authHeader).then(() => {
                    setIsLoading(true);
                    history.push(nextPath);
                    return true;
                }).catch((error) => {
                    error.modalTitle = translator(messages.withdrawErrorTitle);
                    error.modalMessage = translator(messages.withdrawErrorMessage);
                    throw error;
                });
                ModalService.showProgressDialog(messages.withdrawing, promise);
                return promise;
            }
            setIsLoading(true);
            history.push(nextPath);
            return true;
        }, _.noop);
    }, [ModalService, authHeader, history, steps, translator]);

    const handleError = useCallback((error) => {
        console.error(error);
        const errorTitle = error.modalTitle ||translator(messages.genericErrorTitle);
        const errorMessage = error.modalMessage || translator(messages.genericErrorMessage);
        const componentProps = {
            title: translator(errorTitle),
            actionBtnLabel: platformMessages.ok,
            simpleMessage: translator(errorMessage),
            closeSimpleMessage: false
        };

        return modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).then(() => {
            return history.push('/reinstatementContactUs');
        }, _.noop);
    }, [history, translator]);


    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (!initialSubmission) {
        return null;
    }

    return (
        <QuoteInfoComponentContext.Provider value={CustomQuoteInfoComponent}>
            <ErrorBoundary onError={handleError}>
                <Wizard
                    initialSteps={steps}
                    wizardTitle={title}
                    initialData={{
                        submissionVM: initialSubmission,
                        isReinstatement: true
                    }}
                    onCancel={handleOnCancel}
                    onPreviousModalProps={{
                        title: platformMessages.wantToJump,
                        message: platformMessages.wantToJumpMessage,
                        status: 'warning',
                        icon: 'mi-error-outline',
                        confirmButtonText: platformMessages.yes,
                        cancelButtonText: platformMessages.no
                    }}
                    extractVMObject={(data) => _.get(data, 'submissionVM')}
                />
            </ErrorBoundary>
        </QuoteInfoComponentContext.Provider>
    );
}

export default withRouter(withViewModelService(ReinstatementWizard));
